/**
 * Uppsägning av lokalhyresavtal för villkorsändring (12 kap. 58 § JB).
 *
 * Parter / Uppsägning till hyresgästen / Meddelande / Underskrift (hyresvärd)
 * / Erkännande (hyresgäst). The Parter and Uppsägning points are shared with
 * the sibling `uppsagning_avflyttning_lokal`. Every party/property datum is a
 * slot value with an autofill binding.
 *
 * Legal text verbatim from `base-templates/Uppsägning villkorsändring
 * lokal.docx` (Haymanot-approved). DO NOT paraphrase or reorder.
 */
import { group, option, optionList, p, section, strong, t } from '@hyranai/regta-document-ir';
import { dateVal, isFilled, optionChosen, partySlot, propertySlot, selectSlot, shown, signingBlockDefinition, textVal, ticked, whenOption, } from '../common/index.js';
import { boxedRows, letterOpening } from '../andring_hyresvillkor/kit.js';
const LEGAL = {
    plane: 'legal',
    status: 'published',
    language: 'sv-SE',
    legalReview: { reviewedBy: 'Haymanot', source: 'base-templates/Uppsägning villkorsändring lokal.docx' },
};
export const rubrik = {
    ...LEGAL,
    blockKey: 'uppsagning_villkorsandring_lokal.rubrik',
    version: 'v1',
    title: 'Rubrik',
    summary: 'The document title above the first point.',
    slots: [],
    compile: () => letterOpening({ title: 'Uppsägning av lokalhyresavtal för villkorsändring' }),
};
const PARTER_ROWS = [
    ['Hyreskontrakt nr', 'contract_number'],
    ['Fastighetsbeteckning', 'property_designation'],
    ['Hyresvärd', 'landlord_name'],
    ['Personnr / org.nr', 'landlord_org_number'],
    ['Hyresgäst', 'tenant_name'],
    ['Personnr / org.nr', 'tenant_org_number'],
];
const COTENANT_ROWS = [
    ['Hyresgäst (medhyresgäst)', 'cotenant_name'],
    ['Personnr / org.nr', 'cotenant_org_number'],
];
/** Punkt "Parter" — the lease, the property and the parties; the medhyresgäst rows only when one is named. */
export const parter = {
    ...LEGAL,
    blockKey: 'uppsagning_villkorsandring_lokal.parter',
    version: 'v1',
    title: 'Parter',
    summary: 'Contract number, property designation, landlord and tenant(s) with id numbers.',
    slots: [
        {
            key: 'contract_number',
            label: 'Hyreskontrakt nr',
            type: 'text',
            required: true,
            help: 'Numret på det lokalhyresavtal som sägs upp.',
        },
        propertySlot('property_designation', 'Fastighetsbeteckning', 'designation', { required: true }),
        partySlot('landlord_name', 'Namn', 'lessor', 0, 'name', { required: true }),
        partySlot('landlord_org_number', 'Personnr / Org.nr', 'lessor', 0, 'legalId'),
        partySlot('tenant_name', 'Namn', 'lessee', 0, 'name', { required: true }),
        partySlot('tenant_org_number', 'Personnr / Org.nr', 'lessee', 0, 'legalId'),
        partySlot('cotenant_name', 'Namn (medhyresgäst)', 'lessee', 1, 'name'),
        partySlot('cotenant_org_number', 'Personnr / Org.nr (medhyresgäst)', 'lessee', 1, 'legalId'),
    ],
    compile: (input, ctx) => {
        const showCotenant = shown(ctx, isFilled(input.cotenant_name));
        return [section('parter', 'Parter', [boxedRows(input, showCotenant ? [...PARTER_ROWS, ...COTENANT_ROWS] : PARTER_ROWS)])];
    },
};
/** Punkt "Uppsägning till hyresgästen" — the premises and the termination date. */
export const uppsagning = {
    ...LEGAL,
    blockKey: 'uppsagning_villkorsandring_lokal.uppsagning',
    version: 'v1',
    title: 'Uppsägning till hyresgästen',
    summary: 'The premises’ address and municipality and the date the lease ends.',
    slots: [
        propertySlot('property_address', 'Fastighetsadress', 'address', { type: 'textarea', required: true }),
        propertySlot('property_municipality', 'Kommun', 'municipality', { required: true }),
        { key: 'termination_date', label: 'Upphörande den', type: 'date', required: true, help: 'Datum då hyresavtalet upphör att gälla.' },
    ],
    compile: (input) => [
        section('uppsagning', 'Uppsägning till hyresgästen', [
            boxedRows(input, [
                ['Fastighetsadress', 'property_address'],
                ['Kommun', 'property_municipality'],
                ['Upphörande den', 'termination_date'],
            ]),
        ]),
    ],
};
/** "Hyresvärden säger upp hyresavtalet för … till den …." — bold, as in the approved form. */
export function terminationLine(purpose, input, ctx) {
    return p([
        strong([
            t(`Hyresvärden säger upp hyresavtalet för ${purpose} till den `),
            dateVal(ctx, input, 'termination_date'),
            t('.'),
        ]),
    ]);
}
/** `code` is the ADR-0062 code; `value` what the legacy lane stored. */
export const EXTENSION_TERMS_OPTIONS = [
    {
        code: 'forslag_bifogas',
        value: 'alt1',
        label: 'Alt 1 — villkor enligt bifogat förslag till hyresavtal',
        summary: 'Extension terms are set out in an attached draft lease agreement.',
    },
    {
        code: 'andrade_villkor_nedan',
        value: 'alt2',
        label: 'Alt 2 — följande ändrade hyresvillkor (anges nedan)',
        summary: 'Changed lease terms are written directly into the notice below.',
    },
];
const [ALT1, ALT2] = EXTENSION_TERMS_OPTIONS;
export const meddelande = {
    ...LEGAL,
    blockKey: 'uppsagning_villkorsandring_lokal.meddelande',
    version: 'v1',
    title: 'Meddelande',
    summary: 'The termination for changed terms: Alt 1 (attached draft lease) or Alt 2 (changed terms stated), and the 58 b § medling notice.',
    slots: [
        selectSlot('extension_terms_mode', 'För förlängning begärs', EXTENSION_TERMS_OPTIONS, {
            required: true,
            default: ALT1.code,
            help: 'Alt 1: ett förslag till nytt hyresavtal bifogas (lägg till det under Bilagor). ' +
                'Alt 2: de ändrade villkoren skrivs in direkt i meddelandet.',
        }),
        {
            key: 'alt2_changed_terms',
            label: 'Ändrade hyresvillkor',
            type: 'textarea',
            help: 'Beskriv de hyresvillkor som begärs för förlängning.',
            visibleWhen: whenOption('extension_terms_mode', ALT2),
            requiredWhen: whenOption('extension_terms_mode', ALT2),
        },
        {
            key: 'include_unchanged_clause',
            label: 'Lägg till "I övrigt oförändrade villkor"',
            type: 'boolean',
            default: true,
            help: 'Anger att hyresavtalet i övrigt ska löpa på oförändrade villkor. Valfri vid Alt 1, standard vid Alt 2.',
        },
        {
            key: 'delgivning_note',
            label: 'Observera – delgivning',
            type: 'note',
            help: 'Uppsägningen måste vara delgiven hyresgästen senast sista dagen för uppsägning. Säkerställ att delgivning kan styrkas.',
        },
    ],
    compile: (input, ctx) => {
        const isAlt2 = optionChosen(EXTENSION_TERMS_OPTIONS, input.extension_terms_mode, ALT2);
        const showUnchanged = shown(ctx, input.include_unchanged_clause !== false);
        return {
            nodes: [
                section('meddelande', 'Meddelande', [
                    terminationLine('villkorsändring', input, ctx),
                    optionList([
                        option(ticked(ctx, !isAlt2), [strong('Alt 1:'), t(' För förlängning begärs villkor enligt bifogat förslag till hyresavtal.')], {
                            code: ALT1.code,
                        }),
                        {
                            ...option(ticked(ctx, isAlt2), [strong('Alt 2:'), t(' För förlängning begärs följande ändrade hyresvillkor:')], {
                                code: ALT2.code,
                            }),
                            ...(shown(ctx, isAlt2)
                                ? { children: [group([p([textVal(input, 'alt2_changed_terms', 'full')])], { variant: 'boxed' })] }
                                : {}),
                        },
                    ], { sourceKey: 'extension_terms_mode', select: 'single' }),
                    ...(showUnchanged ? [p('I övrigt ska hyresavtalet löpa på oförändrade villkor.')] : []),
                    p('Om förlängning inte kan komma till stånd och ni inte går med på att flytta utan ersättning enligt 12 kap. 58 b § JB, har ni att inom två månader från denna uppsägning hänskjuta tvisten till hyresnämnden för medling.'),
                ]),
            ],
        };
    },
};
/** Shared by both uppsägning variants: landlord signs the uppsägning, tenant(s) sign the receipt. */
export const underskrift = signingBlockDefinition({
    blockKey: 'uppsagning_villkorsandring_lokal.underskrift',
    title: 'Underskrift',
    legalReview: LEGAL.legalReview,
});
export const UPPSAGNING_VILLKORSANDRING_LOKAL_BLOCKS = [rubrik, parter, uppsagning, meddelande, underskrift];
//# sourceMappingURL=blocks.js.map